import { useContext } from 'react';
import { matchSorter } from 'match-sorter';
import AppContext from '../../../common/containers/AppContext';
import { CustomerStatus } from '../../types';
import getPlatforms from '../getPlatforms';

function filterInactiveCustomers(customers) {
    if (!customers?.length) {
        return [];
    }
    return customers.filter(({ status }) => status !== CustomerStatus.INACTIVE);
}

function filterList(list, query) {
    if (!list?.length) {
        return [];
    }
    return matchSorter(list, query ?? '', { keys: ['label'] });
}

function filterPlatforms(families, query) {
    if (!families?.length) {
        return [];
    }

    const platformFamiliesWithStats = families
        .filter(
            (family) =>
                family.platform_family_key &&
                family.key === family.platform_family_key &&
                getPlatforms().find((platform) => platform.key === family.key) !== undefined,
        )
        .map((platformFamily) => {
            return {
                ...platformFamily,
                label:
                    getPlatforms().find((platform) => platform.key === platformFamily.key)?.label ??
                    platformFamily.label,
            };
        });
    return matchSorter(platformFamiliesWithStats, query ?? '', { keys: ['label'] });
}

function filterAllCustomers(allCustomers, query) {
    return matchSorter(allCustomers, query ?? '', { keys: ['label'] });
}

function addRecentlySearchedToFilteredList(filteredList, recentlySearchedIds) {
    if (recentlySearchedIds) {
        const recentlySearchedItems = [];
        recentlySearchedIds.forEach((id) => {
            const recentlySearchedItemIndex = filteredList.findIndex(
                (item) => Number(item.id) === id,
            );
            if (recentlySearchedItemIndex > -1) {
                recentlySearchedItems.push(filteredList.splice(recentlySearchedItemIndex, 1)[0]);
                recentlySearchedItems[recentlySearchedItems.length - 1].isRecentlySearched = true;
            }
        });

        return [...recentlySearchedItems, ...filteredList];
    }

    return filteredList;
}

export default function useGetSearchResults(searchText) {
    const { customers, families, recentlySearchedIdsByType } = useContext(AppContext);
    let allCustomers = [];
    if (customers?.length > 0) {
        allCustomers = [{ id: 0, label: 'All Customers' }];
    }

    let filteredFamilies = filterList(families, searchText);
    const noInactiveCustomers = filterInactiveCustomers(customers);
    let filteredCustomers = filterList(noInactiveCustomers, searchText);
    let filteredPlatforms = filterPlatforms(families, searchText);
    const filteredAllCustomers = filterAllCustomers(allCustomers, searchText);

    // show recently searched items on default with no searchText
    if (!searchText || searchText.trim() === '') {
        filteredFamilies = addRecentlySearchedToFilteredList(
            filteredFamilies,
            recentlySearchedIdsByType?.family,
        );

        filteredCustomers = addRecentlySearchedToFilteredList(
            filteredCustomers,
            recentlySearchedIdsByType?.customer,
        );

        filteredPlatforms = addRecentlySearchedToFilteredList(
            filteredPlatforms,
            recentlySearchedIdsByType?.platform,
        );
    }

    return [filteredFamilies, filteredCustomers, filteredPlatforms, filteredAllCustomers];
}
